import React, { useState, useEffect, useRef } from "react";
import Calendar from "react-calendar";
import "./CalendarWindow.scss";
import { DropDown } from "../DropDown/DropDown";

type View = 'month' | 'year' | 'decade';


declare type CalendarClasses = {
  calendar: string;
  calendar_input: string;
  calendar_label: string;
  //...
};

interface CalendarWindowProps {
  placeholder?: string,
  isLabeled?: boolean,
  className?: string,
  onValidDate?: (isoString: string) => void;
  dateOnly?: boolean;
  classes?: Partial<CalendarClasses>,
  value?: string
}


const CalendarWindow = ({
  placeholder,
  isLabeled = false,
  onValidDate,
  classes,
  dateOnly,
  value
}: CalendarWindowProps) => {

  const [activeStartDate, setActiveStartDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<View>("month");
  const [isOpen, setIsOpen] = useState(false)
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  const [hours, setHours] = useState(now.getHours());
  const [minutes, setMinutes] = useState(now.getMinutes());
  const inputRef = useRef<HTMLInputElement>(null);

  const [selectedUserDate, setSelectedUserDate] = useState<Date | null>(() => {
    if (!placeholder) {
      const currentDate = new Date();
      if (onValidDate) {
        onValidDate(currentDate.toISOString());
      }
      return currentDate;
    }
    return null;
  });
  const MASK = dateOnly ? "__.__.____" : "__.__.____, __:__";
  const [inputValue, setInputValue] = useState(() => {
    if (!placeholder) {
      const currentDate = new Date();
      return formatDateTime(currentDate);
    }
    return MASK;
  });
  const [inputError, setInputError] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const [selectedDateTime, setSelectedDateTime] = useState(() => {
    const d = new Date();
    d.setHours(now.getHours(), now.getMinutes(), 0, 0);
    return d;
  });


  function applyMaskToValue(raw: string) {
    let masked = MASK.split("");
    let rawIndex = 0;
    for (let i = 0; i < masked.length; i++) {
      if (MASK[i] === "_") {
        if (raw[rawIndex]) {
          masked[i] = raw[rawIndex];
          rawIndex++;
        } else {
          masked[i] = "_";
        }
      }
    }
    return masked.join("");
  }

  function getRawFromMasked(masked: string) {
    return masked.replace(/[^0-9]/g, "");
  }

  function isMaskComplete(masked: string) {
    return !masked.includes("_");
  }

  useEffect(() => {
    if (selectedUserDate) {
      setInputValue(formatDateTime(selectedUserDate));
      if (
        selectedUserDate.getFullYear() !== activeStartDate.getFullYear() ||
        selectedUserDate.getMonth() !== activeStartDate.getMonth()
      ) {
        const newStart = new Date(selectedUserDate);
        newStart.setDate(1);
        setActiveStartDate(newStart);
      }
    } else {
      setInputValue(MASK);
    }
  }, [selectedUserDate]);

  const formatDateTime = (date: Date) => {
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    if (dateOnly) {
      return `${day}.${month}.${year}`;
    }
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${day}.${month}.${year}, ${hours}:${minutes}`;
  };

  const parseDateTime = (str: string): Date | null => {
    if (dateOnly) {
      const match = str.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      if (!match) return null;
      const [, dd, mm, yyyy] = match;
      const day = Number(dd);
      const month = Number(mm);
      const year = Number(yyyy);

      if (month < 1 || month > 12 || day < 1 || day > 31) return null;

      const daysInMonth = new Date(year, month, 0).getDate();
      if (day > daysInMonth) return null;

      const date = new Date(year, month - 1, day);
      if (isNaN(date.getTime())) return null;

      return date;
    } else {
      const match = str.match(/^(\d{2})\.(\d{2})\.(\d{4}),\s*(\d{2}):(\d{2})$/);
      if (!match) return null;
      const [, dd, mm, yyyy, hh, min] = match;
      const day = Number(dd);
      const month = Number(mm);
      const year = Number(yyyy);
      const hours = Number(hh);
      const minutes = Number(min);

      if (
        month < 1 || month > 12 ||
        day < 1 || day > 31 ||
        hours < 0 || hours > 23 ||
        minutes < 0 || minutes > 59
      ) return null;

      const daysInMonth = new Date(year, month, 0).getDate();
      if (day > daysInMonth) return null;

      const date = new Date(year, month - 1, day, hours, minutes);
      if (isNaN(date.getTime())) return null;

      return date;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let oldMasked = inputValue;
    let newMasked = e.target.value;
    let selectionStart = e.target.selectionStart || 0;
    let newCursorPos = selectionStart;

    if (newMasked.length < oldMasked.length) {
      let chars = oldMasked.split("");
      if (MASK[selectionStart] === "_") {
        chars[selectionStart] = "_";
        newMasked = chars.join("");
        newCursorPos = selectionStart;
      } else {
        let pos = selectionStart - 1;
        while (pos >= 0 && MASK[pos] !== "_") {
          pos--;
        }
        if (pos >= 0) {
          chars[pos] = "_";
          newMasked = chars.join("");
          newCursorPos = pos;
        } else {
          newMasked = oldMasked;
          newCursorPos = selectionStart;
        }
      }
    } else {
      let inputChar = newMasked[selectionStart - 1];
      let maskIdx = selectionStart - 1;
      if (MASK[maskIdx] === "_" && inputChar && inputChar.match(/\d/)) {
        let chars = oldMasked.split("");
        chars[maskIdx] = inputChar;
        newMasked = chars.join("");

        let nextPos = newMasked.indexOf("_", maskIdx);
        if (nextPos === -1) {
          nextPos = newMasked.length;
        }
        newCursorPos = nextPos;
      } else {
        setInputValue(oldMasked);
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(maskIdx, maskIdx);
          }
        }, 0);
        return;
      }
    }

    setInputValue(newMasked);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);

    if (!isMaskComplete(newMasked)) {
      setInputError(true);
    } else {
      const parsed = parseDateTime(newMasked);
      if (parsed) {
        setSelectedUserDate(parsed);
        if (!dateOnly) {
          setHours(parsed.getHours());
          setMinutes(parsed.getMinutes());
        }
        setInputError(false);
        if (onValidDate) {
          onValidDate(parsed.toISOString());
        }
      } else {
        setInputError(true);
      }
    }
  };

  const changePeriod = (offset: number, view: View) => {
    setActiveStartDate(prev => {
      const newDate = new Date(prev);
      if (view === "month") {
        newDate.setMonth(newDate.getMonth() + offset);
      } else if (view === "year") {
        newDate.setFullYear(newDate.getFullYear() + offset);
      } else if (view === "decade") {
        newDate.setFullYear(newDate.getFullYear() + offset * 10);
      }
      return newDate;
    });
  };

  const incrementHours = () => setHours(h => {
    const newH = (h + 1) % 24;
    updateSelectedDateTime(newH, minutes);
    if (selectedUserDate) {
      const newDate = new Date(selectedUserDate);
      newDate.setHours(newH);
      setSelectedUserDate(newDate);
      if (onValidDate) {
        onValidDate(newDate.toISOString());
      }
    }
    return newH;
  });

  const decrementHours = () => setHours(h => {
    const newH = (h - 1 + 24) % 24;
    updateSelectedDateTime(newH, minutes);
    if (selectedUserDate) {
      const newDate = new Date(selectedUserDate);
      newDate.setHours(newH);
      setSelectedUserDate(newDate);
      if (onValidDate) {
        onValidDate(newDate.toISOString());
      }
    }
    return newH;
  });

  const incrementMinutes = () => setMinutes(m => {
    const newM = (m + 1) % 60;
    updateSelectedDateTime(hours, newM);
    if (selectedUserDate) {
      const newDate = new Date(selectedUserDate);
      newDate.setMinutes(newM);
      setSelectedUserDate(newDate);
      if (onValidDate) {
        onValidDate(newDate.toISOString());
      }
    }
    return newM;
  });

  const decrementMinutes = () => setMinutes(m => {
    const newM = (m - 1 + 60) % 60;
    updateSelectedDateTime(hours, newM);
    if (selectedUserDate) {
      const newDate = new Date(selectedUserDate);
      newDate.setMinutes(newM);
      setSelectedUserDate(newDate);
      if (onValidDate) {
        onValidDate(newDate.toISOString());
      }
    }
    return newM;
  });

  const updateSelectedDateTime = (h: number, m: number) => {
    setSelectedDateTime(prev => {
      const newDate = new Date(prev);
      newDate.setHours(h, m, 0, 0);
      return newDate;
    });
  };

  const handleDayClick = (date: Date) => {
    if (dateOnly) {
      setSelectedDateTime(prev => {
        const newDate = new Date(date);
        return newDate;
      });
      const newUserDate = new Date(date);
      setSelectedUserDate(newUserDate);
      setInputError(false);
      if (onValidDate) {
        onValidDate(newUserDate.toISOString());
      }
    } else {
      setSelectedDateTime(prev => {
        const newDate = new Date(date);
        newDate.setHours(hours, minutes, 0, 0);
        return newDate;
      });
      const newUserDate = new Date(date);
      newUserDate.setHours(hours, minutes, 0, 0);
      setSelectedUserDate(newUserDate);
      setInputError(false);
      if (onValidDate) {
        onValidDate(newUserDate.toISOString());
      }
    }
  };

  const handleNavLabelClick = () => {
    setCurrentView((prev: View) => {
      if (prev === 'month') return 'year';
      if (prev === 'year') return 'decade';
      return 'month';
    });
  };

  const handleClickCalendarWindow = (isOpen: boolean) => {
    if (!isOpen && (!selectedUserDate || inputValue === MASK)) {
      const currentDate = new Date();
      setSelectedUserDate(currentDate);
      setInputValue(formatDateTime(currentDate));
      setInputError(false);
      if (onValidDate) {
        onValidDate(currentDate.toISOString());
      }
    }
  }

  useEffect(() => {
    if (value) {
      const parsed = new Date(value)
      if (!isNaN(parsed.getTime())) {
        setSelectedDateTime(parsed)
        setSelectedUserDate(parsed)
        setHours(parsed.getHours())
        setMinutes(parsed.getMinutes())
        setInputValue(formatDateTime(parsed))
        setInputError(false)
      }
    }
  }, [value])

  const isActive = isFocused || inputValue !== MASK

  return (
    <DropDown
    callback={handleClickCalendarWindow}
      customTrigger={(props) => (<div {...props} className={`calendar-window-wrapper ${classes ? classes.calendar : ""} ${isActive ? 'active' : ''}`}
      >
        <div
          className={`calendar-input${inputError ? " calendar-input--error" : ""} ${classes ? classes.calendar_input : ""}`}
        >
          <input
            type="text"
            className="calendar-input_field"
            placeholder={inputValue === MASK && !isFocused && placeholder && !isLabeled ? placeholder : ''}
            value={inputValue === MASK && !isFocused ? '' : inputValue}
            onChange={handleInputChange}
            ref={inputRef}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
          {placeholder && isLabeled &&
            <label className={`calendar-input_label ${classes ? classes.calendar_label : ''}`}>
              {placeholder}
            </label>}
        </div>
      </div>)}
    >
      {
        <div className="calendar-nav-row-wrapper">
          <div className="calendar-nav-row">
            <span
              className="calendar-nav-label"
              style={{ pointerEvents: 'auto', cursor: 'pointer' }}
              onClick={handleNavLabelClick}
            >
              {(() => {
                if (currentView === 'year') {
                  return activeStartDate.getFullYear();
                } else if (currentView === 'decade') {
                  const startYear = Math.floor(activeStartDate.getFullYear() / 10) * 10;
                  const endYear = startYear + 10;
                  return `${startYear + 1}–${endYear}`;
                } else {
                  const month = activeStartDate.toLocaleString('ru-RU', { month: 'long' });
                  const year = activeStartDate.getFullYear();
                  return `${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`;
                }
              })()}
            </span>
            <span className="calendar-nav-arrows">
              <button
                type="button"
                className="calendar-arrow_prev-month"
                style={{ pointerEvents: 'auto' }}
                onClick={e => {
                  e.stopPropagation();
                  changePeriod(-1, currentView);
                }}
              />
              <button
                type="button"
                className="calendar-arrow_next-month"
                style={{ pointerEvents: 'auto' }}
                onClick={e => {
                  e.stopPropagation();
                  changePeriod(1, currentView);
                }}
              />
            </span>
          </div>
          <Calendar
            value={selectedUserDate || undefined}
            showNavigation={false}
            maxDetail="month"
            minDetail="decade"
            activeStartDate={activeStartDate}
            view={currentView}
            onActiveStartDateChange={({ activeStartDate, view }) => {
              if (activeStartDate) setActiveStartDate(activeStartDate);
              if (view) setCurrentView(view as View);
            }}
            onViewChange={({ view }) => setCurrentView(view as View)}
            onClickDay={handleDayClick}
            formatShortWeekday={() => ""}
            formatMonth={(_locale: string | undefined, date: Date) => {
              const short = date.toLocaleString('ru-RU', { month: 'short' });
              return short.replace('.', '').slice(0, 3).charAt(0).toLowerCase() + short.replace('.', '').slice(1, 3);
            }}
            tileContent={({ date, view }) =>
              view === "month" ? (
                <div className="calendar-month-wrapper">
                  <span
                    className={
                      [
                        "calendar-month-text",
                        (date.getDay() === 0 || date.getDay() === 6) ? "calendar-month-text--weekend" : ""
                      ].filter(Boolean).join(" ")
                    }
                  >
                    {date.toLocaleDateString("ru-RU", { weekday: "short" })}
                  </span>
                </div>
              ) : null
            }
            tileClassName={({ date, view }) => {
              if (view === "month") {
                const currentMonth = activeStartDate.getMonth();
                if (date.getMonth() !== currentMonth) {
                  return "calendar-day--other-month";
                }
              }
              return undefined;
            }}
          />
          {!dateOnly && (
            <div className="calendar-time-picker">
              <div className="calendar-time-button-wrapper">
                <button type="button" onClick={incrementHours} className="calendar-time-button">
                  <span className="calendar-time-arrow-up"></span>
                </button>
                <button type="button" onClick={incrementMinutes} className="calendar-time-button">
                  <span className="calendar-time-arrow-up"></span>
                </button>
              </div>
              <div className="calendar-time-controls">
                <div className="calendar-time-control">
                  <span>{pad(hours)}</span>
                </div>
                <span className="calendar-time-separator">:</span>
                <div className="calendar-time-control">
                  <span>{pad(minutes)}</span>
                </div>
              </div>
              <div className="calendar-time-button-wrapper">
                <button type="button" onClick={decrementHours} className="calendar-time-button">
                  <span className="calendar-time-arrow-down"></span>
                </button>
                <button type="button" onClick={decrementMinutes} className="calendar-time-button">
                  <span className="calendar-time-arrow-down"></span>
                </button>
              </div>
            </div>
          )}
        </div>
      }
    </DropDown>
  );
};

export default CalendarWindow;